
import React, { useState, useEffect, useRef } from 'react';
import { User, Menu, X, Link, LayoutDashboard, ChevronDown, Moon, Sun, LogOut } from 'lucide-react';
import type { PageType } from '../types';

interface HeaderProps {
  onOpenAuth: () => void;
  isLoggedIn: boolean;
  onNavigate: (page: PageType) => void;
  currentPage: PageType;
  isDarkMode: boolean;
  toggleTheme: () => void;
  onLogout: () => void;
}

const Header: React.FC<HeaderProps> = ({ onOpenAuth, isLoggedIn, onNavigate, currentPage, isDarkMode, toggleTheme, onLogout }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      // Threshold adjusted to trigger right after the blue hero section ends
      setIsScrolled(window.scrollY > 680); 
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // User Menu
      if (isUserMenuOpen && userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      
      // Mobile Menu
      if (
        isMobileMenuOpen && 
        mobileMenuRef.current && 
        !mobileMenuRef.current.contains(event.target as Node) &&
        mobileMenuBtnRef.current &&
        !mobileMenuBtnRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isUserMenuOpen || isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isMobileMenuOpen]);

  const tools = [
    { title: 'ابزار ساخت UTM', id: 'utm' as PageType },
    { title: 'QR Code ساز', id: 'qr' as PageType },
    { title: 'بررسی مقصد لینک', id: 'checker' as PageType },
    { title: 'آمار بازدید لینک', id: 'stats' as PageType },
    { title: 'گزارش لینک تخلف', id: 'report' as PageType },
  ];

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 dark:bg-slate-950/90 backdrop-blur-md shadow-sm border-b border-slate-200/50 dark:border-slate-800 py-3'
          : 'bg-transparent py-4 lg:py-6'
      }`}
    >
      <div className="container mx-auto px-4 lg:px-6">
        <div className={`flex items-center justify-between ${isScrolled ? '' : 'lg:bg-white/10 dark:lg:bg-slate-900/40 lg:backdrop-blur-sm lg:rounded-2xl lg:px-4 lg:py-2 lg:border lg:border-white/20 dark:lg:border-white/10'}`}>
          
          {/* Logo */}
          <a 
            href="#" 
            onClick={(e) => { e.preventDefault(); onNavigate('home'); }} 
            className="flex items-center gap-2 group z-50 select-none mr-4 mt-2"
            aria-label="صفحه اصلی فست"
          >
            <span className="text-3xl lg:text-4xl font-black tracking-tighter leading-none text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-blue-600 dark:from-violet-400 dark:to-blue-400 transition-transform duration-300 group-hover:scale-105 font-sans">
              F4ST
            </span>
          </a>

          {/* Desktop Nav - Floating Pill Style */}
          <nav className="hidden lg:flex items-center bg-white/70 dark:bg-slate-900/70 backdrop-blur-md rounded-full px-1.5 py-1.5 border border-white/50 dark:border-slate-700 shadow-sm mx-auto absolute left-1/2 transform -translate-x-1/2">
            <button
              onClick={() => onNavigate('home')}
              className={`relative px-5 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                currentPage === 'home'
                  ? 'text-white bg-slate-900 dark:bg-slate-100 dark:text-slate-900 shadow-md' 
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              صفحه اصلی
            </button>
            
            <div className="relative group">
              <button
                className={`flex items-center gap-1 px-5 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                  ['utm', 'checker', 'stats', 'report', 'qr'].includes(currentPage)
                    ? 'text-violet-700 dark:text-violet-400 bg-violet-50 dark:bg-slate-800'
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
                aria-haspopup="true"
                aria-expanded="false"
              >
                ابزارها
                <ChevronDown size={14} className="mt-0.5 opacity-70" />
              </button>
              
              <div className="absolute top-full right-0 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-2">
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 p-2 min-w-[200px] overflow-hidden">
                  {tools.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => onNavigate(tool.id)}
                      className={`w-full text-right px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                        currentPage === tool.id
                          ? 'bg-violet-50 dark:bg-slate-800 text-violet-700 dark:text-violet-400'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                      }`}
                    >
                      {tool.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={() => onNavigate('about')}
              className={`relative px-5 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                currentPage === 'about'
                  ? 'text-white bg-slate-900 dark:bg-slate-100 dark:text-slate-900 shadow-md' 
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              امکانات
            </button>

            <button
              onClick={() => onNavigate('monetization')}
              className={`relative px-5 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                currentPage === 'monetization'
                  ? 'text-white bg-slate-900 dark:bg-slate-100 dark:text-slate-900 shadow-md' 
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              کسب درآمد
            </button>

            <button
              onClick={() => onNavigate('ads')}
              className={`relative px-5 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                currentPage === 'ads'
                  ? 'text-white bg-slate-900 dark:bg-slate-100 dark:text-slate-900 shadow-md' 
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              تبلیغات
            </button>
          </nav>

          {/* Buttons Group */}
          <div className="flex items-center gap-2 lg:gap-3 z-50">
            {/* Theme Toggle */}
            <button 
              onClick={toggleTheme}
              className="p-2 lg:p-2.5 rounded-xl bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 hover:text-violet-600 dark:hover:text-violet-400 transition-all shadow-sm backdrop-blur-sm"
              aria-label={isDarkMode ? "تغییر به حالت روشن" : "تغییر به حالت تاریک"}
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {isLoggedIn ? (
               <div className="relative" ref={userMenuRef}>
                 <button 
                   onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                   className="hidden lg:flex items-center gap-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 border border-slate-900 dark:border-slate-100 px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-slate-900/20 transition-all hover:bg-slate-800 dark:hover:bg-white hover:-translate-y-0.5 active:scale-95"
                   aria-haspopup="true"
                   aria-expanded={isUserMenuOpen}
                 >
                   <User size={18} />
                   <span>پنل کاربری</span>
                   <ChevronDown size={14} className={`transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                 </button>
                 
                 {/* User Dropdown */}
                 {isUserMenuOpen && (
                   <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 p-2 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 origin-top-left z-[60]">
                     <button
                       onClick={() => { onNavigate('dashboard'); setIsUserMenuOpen(false); }}
                       className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                     >
                       <LayoutDashboard size={16} className="text-violet-600 dark:text-violet-400" />
                       داشبورد
                     </button>
                     <div className="h-px bg-slate-100 dark:bg-slate-800 my-1"></div>
                     <button
                       onClick={() => { onLogout(); setIsUserMenuOpen(false); }}
                       className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                     >
                       <LogOut size={16} />
                       خروج
                     </button>
                   </div>
                 )}
               </div>
            ) : (
              <button 
                onClick={onOpenAuth}
                className="hidden lg:flex items-center gap-2 bg-white dark:bg-slate-800 text-slate-800 dark:text-white border border-slate-200 dark:border-slate-700 px-5 py-2.5 rounded-xl font-bold text-sm shadow-sm transition-all hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-violet-200 dark:hover:border-violet-600 hover:text-violet-700 dark:hover:text-violet-400 hover:shadow-md active:scale-95"
              >
                <User size={18} />
                <span>ورود / ثبت نام</span>
              </button>
            )}

            <button
              ref={mobileMenuBtnRef}
              className="lg:hidden text-slate-700 dark:text-slate-200 p-2 bg-white/60 dark:bg-slate-800/60 rounded-xl backdrop-blur-md border border-slate-200 dark:border-slate-700 shadow-sm hover:bg-white dark:hover:bg-slate-800 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="منوی موبایل"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          ref={mobileMenuRef}
          className={`lg:hidden absolute left-4 right-4 top-20 transition-all duration-300 transform origin-top ${isMobileMenuOpen ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible'}`}
        >
          <div className="bg-white/95 dark:bg-slate-950/95 backdrop-blur-2xl rounded-[1.5rem] p-4 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] border border-white/20 dark:border-slate-800 ring-1 ring-slate-900/5 overflow-hidden">
            <nav className="flex flex-col space-y-1">
              <button
                onClick={() => { onNavigate('home'); setIsMobileMenuOpen(false); }}
                className={`px-4 py-3 rounded-xl font-bold text-right transition-colors ${currentPage === 'home' ? 'bg-violet-50 dark:bg-slate-800 text-violet-700 dark:text-violet-400' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'}`}
              >
                صفحه اصلی
              </button>
              
              <div className="bg-slate-50/80 dark:bg-slate-900/50 rounded-2xl p-2 my-1">
                <div className="px-3 py-1.5 text-xs font-bold text-slate-400 uppercase tracking-wider">ابزارها</div>
                <div className="grid grid-cols-1 gap-1">
                  {tools.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => { onNavigate(tool.id); setIsMobileMenuOpen(false); }}
                      className={`w-full text-right px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                        currentPage === tool.id ? 'bg-white dark:bg-slate-800 text-violet-700 dark:text-violet-400 shadow-sm' : 'text-slate-600 dark:text-slate-300 hover:bg-white/50 dark:hover:bg-slate-800/50'
                      }`}
                    >
                      {tool.title}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => { onNavigate('about'); setIsMobileMenuOpen(false); }}
                className={`px-4 py-3 rounded-xl font-bold text-right transition-colors ${currentPage === 'about' ? 'bg-violet-50 dark:bg-slate-800 text-violet-700 dark:text-violet-400' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'}`}
              >
                امکانات سایت
              </button>

              <button
                onClick={() => { onNavigate('monetization'); setIsMobileMenuOpen(false); }}
                className={`px-4 py-3 rounded-xl font-bold text-right transition-colors ${currentPage === 'monetization' ? 'bg-violet-50 dark:bg-slate-800 text-violet-700 dark:text-violet-400' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'}`}
              >
                کسب درآمد
              </button>

              <button
                onClick={() => { onNavigate('ads'); setIsMobileMenuOpen(false); }}
                className={`px-4 py-3 rounded-xl font-bold text-right transition-colors ${currentPage === 'ads' ? 'bg-violet-50 dark:bg-slate-800 text-violet-700 dark:text-violet-400' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900'}`}
              >
                تبلیغات
              </button>
              
              <div className="h-px bg-slate-100 dark:bg-slate-800 my-2"></div>
              
              {isLoggedIn ? (
                <>
                  <button 
                    onClick={() => { onNavigate('dashboard'); setIsMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-2 w-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-6 py-4 rounded-xl font-bold shadow-lg shadow-slate-900/20 active:scale-95 transition-transform mt-2"
                  >
                    <LayoutDashboard size={18} />
                    <span>داشبورد من</span>
                  </button>
                  <button 
                    onClick={() => { onLogout(); setIsMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-2 w-full bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 px-6 py-4 rounded-xl font-bold active:scale-95 transition-transform mt-2"
                  >
                    <LogOut size={18} />
                    <span>خروج از حساب</span>
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => { setIsMobileMenuOpen(false); onOpenAuth(); }}
                  className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-violet-600 to-blue-600 text-white px-6 py-4 rounded-xl font-bold shadow-lg shadow-violet-500/20 active:scale-95 transition-transform mt-2"
                >
                  <User size={18} />
                  <span>ورود / ثبت نام</span>
                </button>
              )}
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
